import { makeAutoObservable } from 'mobx';
import createStoreContext from '../../stores/createStoreContext';
import {
  fetchTags,
  createTag,
  updateTagById,
  deleteTagById,
} from 'src/services/tag';
import {
  TagForDataGrid,
  CreateTagPayload,
  UpdateTagPayload,
} from 'src/types/tag';
import { toUpperCaseColor } from 'src/utils/color';
import { DEFAULT_TAG_COLOR } from 'src/constants/tag';

const DEFAULT_LIMIT = 10;
const TAG_CACHE_TTL_MS = 60_000;
const TAG_CACHE_QUERY_SEPARATOR = '::';

type TagCacheEntry = {
  tags: TagForDataGrid[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  fetchedAt: number;
};

class TagStore {
  tags: TagForDataGrid[] = [];
  isLoading = false;
  totalCount = 0;
  currentPage = 1;
  totalPages = 1;
  private cacheByQuery = new Map<string, TagCacheEntry>();
  private inflightRequests = new Map<string, Promise<void>>();
  private latestRequestKey: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  private invalidateCache = () => {
    this.cacheByQuery.clear();
  };

  loadTags = async (
    query: string,
    page: number = 1,
    limit: number = DEFAULT_LIMIT,
    options: { force?: boolean } = {},
  ) => {
    const normalizedQuery = query.trim();
    const cacheKey = [normalizedQuery, String(page), String(limit)].join(
      TAG_CACHE_QUERY_SEPARATOR,
    );
    const cached = this.cacheByQuery.get(cacheKey);
    const now = Date.now();
    const isCacheValid = cached && now - cached.fetchedAt < TAG_CACHE_TTL_MS;

    if (isCacheValid && !options.force) {
      this.tags = cached.tags;
      this.totalCount = cached.totalCount;
      this.currentPage = cached.currentPage;
      this.totalPages = cached.totalPages;

      return;
    }

    if (!options.force && this.inflightRequests.has(cacheKey)) {
      await this.inflightRequests.get(cacheKey);

      return;
    }

    const requestKey = cacheKey;

    this.latestRequestKey = requestKey;

    this.isLoading = true;

    const requestPromise = (async () => {
      try {
        const { success, content } = await fetchTags(
          page,
          limit,
          normalizedQuery,
        );

        if (!success || !content) {
          if (this.latestRequestKey === requestKey) {
            this.tags = [];
            this.totalCount = 0;
            this.currentPage = page;
            this.totalPages = 1;
          }

          return;
        }

        const tags = content.data.map((tag) => ({
          id: tag._id,
          ...tag,
          color: toUpperCaseColor(tag.color) || DEFAULT_TAG_COLOR,
        }));
        const totalCount = content.totalCount;
        const totalPages = Math.ceil(totalCount / limit);

        if (this.latestRequestKey !== requestKey) {
          return;
        }

        this.tags = tags;
        this.totalCount = totalCount;
        this.currentPage = page;
        this.totalPages = totalPages;
        this.cacheByQuery.set(cacheKey, {
          tags,
          totalCount,
          currentPage: page,
          totalPages,
          fetchedAt: Date.now(),
        });
      } finally {
        this.inflightRequests.delete(requestKey);
        this.isLoading = this.inflightRequests.size > 0;
      }
    })();

    this.inflightRequests.set(requestKey, requestPromise);
    await requestPromise;
  };

  addTag = async (payload: CreateTagPayload) => {
    const { success, content } = await createTag(payload);

    if (success && content) {
      this.invalidateCache();
      const newTag = {
        id: content._id,
        ...content,
        color: toUpperCaseColor(content.color) || DEFAULT_TAG_COLOR,
      };

      this.tags = [newTag, ...this.tags];
      this.totalCount += 1;
    }

    return success;
  };

  updateTag = async (id: string, payload: UpdateTagPayload) => {
    const { success, content } = await updateTagById(id, payload);

    if (success && content) {
      this.invalidateCache();
      const updatedTag = {
        id: content._id,
        ...content,
        color: toUpperCaseColor(content.color) || DEFAULT_TAG_COLOR,
      };

      this.tags = this.tags.map((tag) => (tag._id === id ? updatedTag : tag));
    }

    return success;
  };

  deleteTag = async (id: string) => {
    const { success } = await deleteTagById(id);

    if (success) {
      this.invalidateCache();
      this.tags = this.tags.filter((tag) => tag._id !== id);
      this.totalCount = Math.max(0, this.totalCount - 1);
    }

    return success;
  };
}

export const [TagStoreProvider, useTagStore] = createStoreContext(TagStore);
export type TagStoreType = ReturnType<typeof useTagStore>;
